import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import SEOHead from "./components/SEOHead";
import HomePage from "./pages/HomePage";
import LandingPage from "./pages/LandingPage";
import EventsPage from "./pages/EventsPage";
import FAQPage from "./pages/FAQPage";
import PlatformGuidesPage from "./pages/PlatformGuidesPage";
import ContactPage from "./pages/ContactPage";
import SettingsPage from "./pages/SettingsPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";
import BusinessDashboard from "./pages/BusinessDashboard";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import TransactionDetailPage from "./pages/TransactionDetailPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ConfirmTransactionPage from "./pages/ConfirmTransactionPage";
import SubmitGroupPage from "./pages/SubmitGroupPage";
import FacebookGroupsPage from "./pages/FacebookGroupsPage";
import FraudCasesPage from "./pages/FraudCasesPage";
import FraudCaseDetailPage from "./pages/FraudCaseDetailPage";
import AntiFraudPage from "./pages/AntiFraudPage";
import FraudCasesAdminPage from "./pages/FraudCasesAdminPage";
import FraudStatisticsPage from "./pages/FraudStatisticsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import DataManagementPage from "./pages/DataManagementPage";
import PartnerDashboardPage from "./pages/PartnerDashboardPage";
import BecomePartnerPage from "./pages/BecomePartnerPage";
import PublicListingPage from "./pages/PublicListingPage";
import EventDetailsPage from "./pages/EventDetailsPage";
import CookieConsent from "./components/CookieConsent";
import ScrollToTop from "./components/ScrollToTop";
import FloatingTransferButton from "./components/FloatingTransferButton";
import FloatingSupportButton from "./components/FloatingSupportButton";
import PopupNotification from "./components/PopupNotification";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Token refresh logic
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Axios interceptor to add auth token and handle refresh
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Axios response interceptor for token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If token expired and not already retrying
    if (error.response?.status === 401 && 
        error.response?.data?.detail === "Token expired" && 
        !originalRequest._retry) {
      
      if (isRefreshing) {
        // Queue the request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const refreshToken = localStorage.getItem("refreshToken");
        const response = await axios.post(`${API}/auth/refresh`, {
          refresh_token: refreshToken
        });
        
        const { access_token, refresh_token } = response.data;
        localStorage.setItem("token", access_token);
        if (refresh_token) {
          localStorage.setItem("refreshToken", refresh_token);
        }
        
        processQueue(null, access_token);
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Clear tokens and redirect to login
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/auth";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to refresh user data (useful after role changes)
  const refreshUserData = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        return response.data;
      } catch (error) {
        localStorage.removeItem("token");
        setUser(null);
        return null;
      }
    }
    return null;
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem("token");
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <ThemeProvider user={user}>
        <div className="App">
          <Toaster position="top-right" richColors />
          <BrowserRouter>
            <SEOHead />
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<LandingPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
              <Route path="/auth" element={<HomePage user={user} setUser={setUser} />} />
              <Route path="/login" element={<HomePage user={user} setUser={setUser} />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/confirm-transaction" element={<ConfirmTransactionPage />} />
            <Route path="/events" element={<EventsPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/faq" element={<FAQPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/platform-guides" element={<PlatformGuidesPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/guides-plateformes" element={<PlatformGuidesPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/contact" element={<ContactPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/submit-group" element={<SubmitGroupPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/facebook-groups" element={<FacebookGroupsPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            {/* Partner Program */}
            <Route path="/devenir-partenaire" element={<BecomePartnerPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/billet/:slug" element={<PublicListingPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/event/:id" element={<EventDetailsPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/become-partner" element={<BecomePartnerPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            {/* Fraud Cases Routes */}
            <Route path="/fraudes" element={<FraudCasesPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/fraudes/:slug" element={<FraudCaseDetailPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/anti-fraude" element={<AntiFraudPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/fraud-statistics" element={<FraudStatisticsPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/statistiques-fraude" element={<FraudStatisticsPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route
              path="/admin/fraudes"
              element={
                user && user.role === "super_admin" ? (
                  <FraudCasesAdminPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/dashboard"
              element={
                user ? (
                  <DashboardPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin"
              element={
                user && (user.role === "admin" || user.role === "super_admin") ? (
                  <SuperAdminDashboard user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/settings"
              element={
                user && user.role === "super_admin" ? (
                  <SettingsPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />

            <Route
              path="/business"
              element={
                user && user.role === "owner" ? (
                  <BusinessDashboard user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/super-admin"
              element={
                user && (user.role === "admin" || user.role === "super_admin") ? (
                  <SuperAdminDashboard user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/partner-dashboard"
              element={
                user && user.role?.includes('partner') ? (
                  <PartnerDashboardPage user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/dashboard" />
                )
              }
            />
            <Route
              path="/transactions/:id"
              element={
                user ? (
                  <TransactionDetailPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            {/* Law 25 - Privacy Pages */}
            <Route path="/politique-confidentialite" element={<PrivacyPolicyPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/privacy" element={<PrivacyPolicyPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/conditions-utilisation" element={<TermsOfServicePage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route path="/terms" element={<TermsOfServicePage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />} />
            <Route
              path="/mes-donnees"
              element={
                user ? (
                  <DataManagementPage user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
          </Routes>
          {/* Popup Notifications - shown after login */}
          {user && <PopupNotification user={user} />}
          {/* Floating Transfer Button - visible on all pages */}
          <FloatingTransferButton />
          {/* Floating Support Button - visible on all pages */}
          <FloatingSupportButton />
          {/* Cookie Consent Banner - Law 25 Compliance */}
          <CookieConsent />
        </BrowserRouter>
      </div>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;